import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./App.css";
import {
  Button,
  Center,
  ColorScheme,
  ColorSchemeProvider,
  MantineProvider,
  Paper,
  Stepper,
  Text,
  Title,
} from "@mantine/core";
import { IconCircleCheck } from "@tabler/icons-react";
import { useLocalStorage, useMediaQuery } from "@mantine/hooks";
import LightAndDarkModeButton from "./Components/LightDarkButton";
import StepGetStarted from "./Components/StepGetStarted";
import StepEmail from "./Components/StepEmail";
import StepPrivateUsername from "./Components/StepPrivateUsername";
import StepLocation from "./Components/StepLocation";
import CenterIdentity from "centeridentity";
import { Notifications } from "@mantine/notifications";
import Finish from "./Components/Finish";
import PrevNextFinish from "./Components/PrevNextFinish";
import Logo from "./Components/Logo";

let API_KEY: string;
let API_SERVER: string;

if (window.location.hostname === "localhost") {
  API_KEY =
    "MEUCIQCIzIDpRwBJgU0fjTh6FZhpIrLz/WNTLIZwK2Ifx7HjtQIgfYYOPFy7ypU+KYeYzkCa9OWwbwPIt9Hk0cV8Q6pcXog=";
  API_SERVER = "http://localhost:8005";
} else {
  API_KEY =
    "MEQCIC7ADPLI3VPDNpQPaXAeB8gUk2LrvZDJIdEg9C12dj5PAiB61Te/sen1D++EJAcgnGLH4iq7HTZHv/FNByuvu4PrrA==";
  API_SERVER = "https://centeridentity.com";
}

function makeUrl(path: string, extraQueryParams: string = "") {
  return `${API_SERVER}${path}?api_key=${API_KEY}&origin=${window.location.origin}&${extraQueryParams}`;
}

function App() {
  const [colorScheme, setColorScheme] = useLocalStorage<ColorScheme>({
    key: "mantine-color-scheme",
    defaultValue: "light",
  });

  const [rws, setWebSocket] = useState(null);
  const queryArgs = new URLSearchParams(window.location.search);
  const ci = new CenterIdentity({
    strength: "low",
    api_key: queryArgs.get("api_key")?.replaceAll(" ", "+"),
    precision: 4,
    url_prefix: API_SERVER,
  });

  const [opened, setOpened] = useState(false);

  const [[step, direction], setStep] = useState([0, 0]);

  const [place, setPlaces] = useState({ label: "Portland, Oregon" });

  const [publicUsername, setPublicUsername] = useState(
    queryArgs.get("public_username") || ""
  );
  const [privateUsername, setPrivateUsername] = useState("");
  const [privateUsernameHint, setPrivateUsernameHint] = useState("");
  const [selectedLocation, setSelectedLocation] = useState({
    lat: "",
    lng: "",
  });
  const [selectedLocationHint, setSelectedLocationHint] = useState("");
  const [identity, setIdentity] = useState(null);
  const [publicIdentity, setPublicIdentity] = useState(null);
  const [formIsValid, setFormIsValid] = useState(false);
  if (queryArgs.get("public_username") !== null && !formIsValid)
    setFormIsValid(true);

  const [tier, setTier] = useState("");

  const [regType, setRegType] = useState("individual");
  const [upload, setUpload] = useState(false);
  const [businessUpload, setBusinessUpload] = useState(false);

  const [identityScanned, setIdentityScanned] = useState(false);
  const [asset, setAsset] = useState({ asset: "" });

  const matches = useMediaQuery("(min-width: 56.25em)");

  const toggleColorScheme = (value?: ColorScheme) =>
    setColorScheme(value || (colorScheme === "dark" ? "light" : "dark"));

  const showMap = false;

  const variants = {
    enter: (direction: number) => {
      return {
        x: direction > 0 ? -1000 : -1000,
        opacity: 0,
      };
    },
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => {
      return {
        zIndex: 0,
        x: direction < 0 ? 1000 : -1000,
        opacity: 0,
      };
    },
  };

  const [center, setCenter] = useState({
    lat: -3.745,
    lng: -38.523,
  });
  const [zoom, setZoom] = useState(2.5);
  const [challengeContext, setChallengeContext] = useState("");
  const paginate = async (newDirection: number) => {
    if (step === lastStep && newDirection === -1) {
      setTimeout(() => {
        setSelectedLocation({ lat: "", lng: "" });
      }, 500);
      setTimeout(() => {
        setSelectedLocation(selectedLocation);
      }, 1000);
      setTimeout(() => {
        setCenter(selectedLocation as any);
        setZoom(7);
      }, 1500);
    }
    if (step + newDirection <= lastStep) {
      setStep([step + newDirection, newDirection]);
    }
    if (step + newDirection === lastStep) {
      if (queryArgs.get("mode") === "auth") {
        let privateIdentity;
        try {
          privateIdentity = await ci.get(
            privateUsername,
            selectedLocation.lat,
            selectedLocation.lng
          );
        } catch (err) {
          privateIdentity = await ci.setFromNew(
            privateUsername,
            selectedLocation.lat,
            selectedLocation.lng
          );
        }
        setIdentity(privateIdentity);
        const publicIdentity = await ci.reviveUser(
          privateIdentity.wif,
          useWallet ? privateIdentity.address : publicUsername
        );
        setPublicIdentity(publicIdentity);
        localStorage.setItem("identity", ci.toJson(publicIdentity));
        const data: any = await ci.authenticate(makeUrl("/challenge"));
        setChallengeContext(JSON.stringify(data));
      }
    }
  };

  window.addEventListener("message", (e) => {
    // Get the sent data
    const data = e.data;
    console.log(data);
    if (data.asset) {
      setAsset({ ...data });
    }
    // If you encode the message in JSON before sending them,
    // then decode here
    // const decoded = JSON.parse(data);
  });

  const [useWallet, setUseWallet] = useState(false);

  const changeUseWallet = (uw: boolean) => {
    setPublicUsername("");
    setUseWallet(uw);
  };
  const lastStep = useWallet ? 2 : 3;
  return (
    <ColorSchemeProvider
      colorScheme={colorScheme}
      toggleColorScheme={toggleColorScheme}
    >
      <MantineProvider
        withGlobalStyles
        withNormalizeCSS
        theme={{ colorScheme }}
      >
        <Notifications />
        <>
          <Center>
            <Logo />
          </Center>
          <Center>
            <LightAndDarkModeButton />
          </Center>
          <AnimatePresence initial={false} custom={direction}>
            {[
              <StepEmail
                setPublicUsername={setPublicUsername}
                publicUsername={publicUsername}
                paginate={paginate}
                setFormIsValid={setFormIsValid}
              />,
              <StepPrivateUsername
                publicUsername={publicUsername}
                setPrivateUsername={setPrivateUsername}
                privateUsername={privateUsername}
                setPrivateUsernameHint={setPrivateUsernameHint}
                privateUsernameHint={privateUsernameHint}
                paginate={paginate}
                matches={matches}
              />,
              <StepLocation
                setPlaces={setPlaces}
                place={place}
                publicUsername={publicUsername}
                setSelectedLocation={setSelectedLocation}
                selectedLocation={selectedLocation}
                setSelectedLocationHint={setSelectedLocationHint}
                selectedLocationHint={selectedLocationHint}
                center={center}
                zoom={zoom}
              />,
              <Finish />,
            ].map((item, key) => (
              <div key={key}>
                {key === step ? (
                  <motion.div
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                  >
                    <Center>
                      <Paper
                        shadow="xs"
                        p="xl"
                        mt="xl"
                        radius="md"
                        mx="xl"
                        mb="xl"
                        withBorder
                      >
                        {item}
                        <PrevNextFinish
                          paginate={paginate}
                          step={step}
                          lastStep={lastStep}
                          queryArgs={queryArgs}
                          challengeContext={challengeContext}
                          privateUsernameHint={privateUsernameHint}
                          selectedLocationHint={selectedLocationHint}
                          asset={asset}
                          ci={ci}
                          privateUsername={privateUsername}
                          selectedLocation={selectedLocation}
                          setAsset={setAsset}
                          makeUrl={makeUrl}
                          formIsValid={formIsValid}
                        />
                      </Paper>
                    </Center>
                  </motion.div>
                ) : null}
              </div>
            ))}
          </AnimatePresence>
        </>
      </MantineProvider>
    </ColorSchemeProvider>
  );
}

export default App;
